import { useState, useEffect, useCallback, useRef } from 'react';

// USB barcode scanners typically act as keyboard input
// They send characters rapidly followed by Enter key

interface UseBarcodeScannnerOptions {
  onScan: (barcode: string) => void;
  onError?: (error: string) => void;
  enabled?: boolean;
  minLength?: number;        // Minimum barcode length (default: 5)
  maxLength?: number;        // Maximum barcode length (default: 100)
  timeout?: number;          // Reset buffer after this many ms of no input (default: 100)
  preventDefault?: boolean;  // Prevent default on Enter key (default: true)
}

interface UseBarcodeScannnerReturn {
  lastScanned: string | null;
  isScanning: boolean;
  manualEntry: string;
  setManualEntry: (value: string) => void;
  submitManualEntry: () => void;
  clearLastScanned: () => void;
}

export function useBarcodeScanner(options: UseBarcodeScannnerOptions): UseBarcodeScannnerReturn {
  const {
    onScan,
    onError,
    enabled = true,
    minLength = 5,
    maxLength = 100,
    timeout = 100,
    preventDefault = true,
  } = options;

  const [lastScanned, setLastScanned] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [manualEntry, setManualEntry] = useState('');

  // Refs to track buffer and timing
  const bufferRef = useRef<string>('');
  const lastKeyTimeRef = useRef<number>(0);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the buffer
  const clearBuffer = useCallback(() => {
    bufferRef.current = '';
    setIsScanning(false);
  }, []);

  // Process the scanned barcode
  const processBarcode = useCallback((barcode: string) => {
    const trimmed = barcode.trim();

    if (trimmed.length < minLength) {
      onError?.(`Barcode too short (min ${minLength} characters)`);
      return;
    }

    if (trimmed.length > maxLength) {
      onError?.(`Barcode too long (max ${maxLength} characters)`);
      return;
    }

    setLastScanned(trimmed);
    setManualEntry('');
    onScan(trimmed);
  }, [minLength, maxLength, onScan, onError]);

  // Handle manual entry submission
  const submitManualEntry = useCallback(() => {
    if (manualEntry.trim()) {
      processBarcode(manualEntry);
      setManualEntry('');
    }
  }, [manualEntry, processBarcode]);

  // Clear last scanned
  const clearLastScanned = useCallback(() => {
    setLastScanned(null);
  }, []);

  // Keyboard event handler
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isInputField = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;

      const now = Date.now();
      const timeSinceLastKey = now - lastKeyTimeRef.current;

      // Detect rapid typing (scanner behavior) - scanners type faster than 50ms between keys
      const isRapidInput = timeSinceLastKey < 50;

      // If too much time has passed, reset buffer
      if (timeSinceLastKey > timeout && bufferRef.current.length > 0) {
        clearBuffer();
      }

      lastKeyTimeRef.current = now;

      // Handle Enter key - submit the barcode
      if (event.key === 'Enter') {
        // Prefer the input field value if focused (scanner may have typed into it)
        // This avoids the first-character-missing issue with the buffer
        const inputValue = (isInputField && target instanceof HTMLInputElement) ? target.value.trim() : '';
        const barcode = inputValue.length >= minLength ? inputValue : bufferRef.current;

        if (barcode.length >= minLength) {
          event.preventDefault();
          event.stopPropagation();
          processBarcode(barcode);
          clearBuffer();
          // Clear the input field if we're in one (scanner typed into it)
          if (isInputField && target instanceof HTMLInputElement) {
            target.value = '';
            target.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
        return;
      }

      // Capture characters for scanner detection
      // Either: not in input field, OR rapid input (scanner), OR scanning already in progress
      if (!isInputField || isRapidInput || isScanning) {
        if (event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
          bufferRef.current += event.key;
          setIsScanning(true);

          // Clear any existing timeout
          if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
          }

          // Set timeout to clear buffer if no more input
          timeoutIdRef.current = setTimeout(() => {
            if (bufferRef.current.length > 0 && bufferRef.current.length < minLength) {
              // Partial scan, clear it
              clearBuffer();
            }
          }, timeout * 2);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [enabled, minLength, timeout, preventDefault, isScanning, manualEntry, processBarcode, clearBuffer, submitManualEntry]);

  return {
    lastScanned,
    isScanning,
    manualEntry,
    setManualEntry,
    submitManualEntry,
    clearLastScanned,
  };
}

export default useBarcodeScanner;
